import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { phappsRequest } from "@/services/apiService";
import {
  API_PATHS,
  buildGroupManagerLinkedChemistsPath,
  buildGroupManagerDeletePath,
} from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import CreateGroupManagerModal from "@/components/modals/admin/CreateGroupManagerModal";
import EditGroupManagerModal from "@/components/modals/admin/EditGroupManagerModal";
import AddGroupManagerChemistsModal from "@/components/modals/admin/AddGroupManagerChemistsModal";
import GroupManagerRoster from "@/components/admin/GroupManagerRoster";
import useAlert from "@/components/feedback/useAlert";

const GOLD = PHARMACY_HALO_COLORS.YELLOW; // #FFAF3C — selected-row tint

// Solid Pharmacy Halo navy (#002169) button; Tailwind needs literal class
// strings, so the hex is inlined rather than pulled from colors.js.
const btnNavy =
  "rounded-lg bg-[#002169] text-white font-semibold hover:bg-[#003399] disabled:opacity-50";
// Quiet outline variant for secondary controls (select all / clear).
const btnGhostNavy =
  "rounded-md border border-[#002169]/25 bg-white text-[#002169] hover:bg-[#002169]/5 disabled:opacity-50";

function extractArray(data, keys) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  for (const k of keys) {
    if (Array.isArray(data[k])) return data[k];
  }
  return [];
}

function managerId(m) {
  return m?.Id ?? m?.id;
}

function chemistRowId(c) {
  return c?.ChemistID ?? c?.chemistID;
}

function filterChemistRows(rows, q) {
  const s = q.trim().toLowerCase();
  if (!s) return rows;
  return rows.filter((r) => {
    const name = String(r.ChemistName ?? r.chemistName ?? "").toLowerCase();
    const email = String(r.Email ?? r.email ?? "").toLowerCase();
    return name.includes(s) || email.includes(s);
  });
}

// "Last used their key" — relative for recent activity, plain date beyond a week.
function formatLastAccess(value) {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function lastAccessTitle(value) {
  if (!value) return "Has never used the portal";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-AU");
}

function IconPencil({ className }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
  );
}

function IconUsers({ className }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
    </svg>
  );
}

function IconTrash({ className }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function IconBtn({ title, onClick, disabled, danger, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border border-transparent transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        danger
          ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
          : "text-gray-400 hover:text-[#002169] hover:bg-[#002169]/5"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * One of the two stacked manager grids ("Pharmacy Halo Admin" / "Pharmacy
 * Group Staff"). Staff rows drop the task-colour dot and gain a linked-chemist
 * count; neither shows the old Comment column.
 */
function ManagerTable({
  title,
  rows,
  emptyText,
  showDot,
  showChemistCount,
  selectedId,
  ownManagerId,
  onSelect,
  onEdit,
  onDelete,
}) {
  return (
    <section className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <span className="text-xs text-gray-500">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-gray-500 text-sm">{emptyText}</div>
      ) : (
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-700 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 font-medium">Name</th>
              {showChemistCount && (
                <th className="px-3 py-2 font-medium text-center whitespace-nowrap">
                  Chemists
                </th>
              )}
              <th className="px-3 py-2 font-medium whitespace-nowrap">Last access</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((m) => {
              const id = managerId(m);
              const activeRow = selectedId === id;
              const isSelf = ownManagerId != null && Number(id) === Number(ownManagerId);
              return (
                <tr
                  key={id}
                  className="cursor-pointer hover:bg-gray-50"
                  style={activeRow ? { backgroundColor: `${GOLD}2e` } : undefined}
                  onClick={() => onSelect(m)}
                >
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2 font-medium text-gray-900">
                      {showDot && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-black/5"
                          style={{ backgroundColor: m.task_color || "#E5E7EB" }}
                          title={
                            m.task_color
                              ? `Task colour ${m.task_color}`
                              : "No task colour set"
                          }
                        />
                      )}
                      {m.Name || "—"}
                      {m.IsActive === false && (
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          inactive
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-gray-400 break-all">
                      {m.Email || ""}
                    </span>
                  </td>
                  {showChemistCount && (
                    <td className="px-3 py-2 text-center text-gray-700">
                      {m.LinkedChemistCount ?? 0}
                    </td>
                  )}
                  <td
                    className="px-3 py-2 text-gray-600 whitespace-nowrap"
                    title={lastAccessTitle(m.LastAccessAt)}
                  >
                    {formatLastAccess(m.LastAccessAt)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <IconBtn
                      title="View linked chemists"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(m);
                      }}
                    >
                      <IconUsers className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn
                      title="Edit manager"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(m);
                      }}
                    >
                      <IconPencil className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn
                      danger
                      title={isSelf ? "You can't delete your own account" : "Delete manager"}
                      disabled={isSelf}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(m);
                      }}
                    >
                      <IconTrash className="w-4 h-4" />
                    </IconBtn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

const GroupManagersPage = () => {
  const { showAlert, AlertSnackbar } = useAlert();

  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(true);
  const [managersError, setManagersError] = useState(null);
  const [activeOnly, setActiveOnly] = useState(true);
  // "managers" = the per-person detail view; "roster" = the Manager Options matrix.
  const [view, setView] = useState("managers");

  // Slack connect column is PH-Admin-viewer only; own row connects directly,
  // other rows copy a send-to-them link (see GroupManagerRoster).
  const viewerIsPHAdmin = sessionStorage.getItem("IsPHAdmin") === "true";
  const ownManagerId = useMemo(() => {
    try {
      return Number(jwtDecode(localStorage.getItem("jwtToken"))?._id) || null;
    } catch {
      return null;
    }
  }, []);

  const [selectedManager, setSelectedManager] = useState(null);
  const [linkedChemists, setLinkedChemists] = useState([]);
  const [allChemists, setAllChemists] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [addChemistsOpen, setAddChemistsOpen] = useState(false);

  const [linkedSearch, setLinkedSearch] = useState("");
  const [linkedSelected, setLinkedSelected] = useState(() => new Set());
  const [addSaving, setAddSaving] = useState(false);
  const [removeSaving, setRemoveSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const visibleManagers = useMemo(() => {
    if (!activeOnly) return managers;
    return managers.filter((m) => m.IsActive !== false);
  }, [managers, activeOnly]);

  const phAdmins = useMemo(
    () => visibleManagers.filter((m) => !!m.IsPHAdmin),
    [visibleManagers]
  );
  const groupStaff = useMemo(
    () => visibleManagers.filter((m) => !m.IsPHAdmin),
    [visibleManagers]
  );

  useEffect(() => {
    if (!activeOnly || !selectedManager) return;
    if (selectedManager.IsActive === false) setSelectedManager(null);
  }, [activeOnly, selectedManager]);

  const loadManagers = useCallback(async () => {
    setManagersLoading(true);
    setManagersError(null);
    try {
      const res = await phappsRequest(API_PATHS.GROUP_MANAGERS_LIST, "GET");
      const list = extractArray(res, [
        "managers",
        "groupManagers",
        "Managers",
        "items",
        "data",
        "results",
      ]);
      setManagers(list);
      setSelectedManager((prev) => {
        if (!prev) return null;
        const id = managerId(prev);
        const next = list.find((m) => managerId(m) === id);
        return next || null;
      });
    } catch (e) {
      setManagersError(e.message || "Failed to load managers.");
      setManagers([]);
    } finally {
      setManagersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManagers();
  }, [loadManagers]);

  const loadManagerChemists = useCallback(async (mgr) => {
    const id = managerId(mgr);
    if (id == null) return;
    setDetailLoading(true);
    setDetailError(null);
    setLinkedSearch("");
    setLinkedSelected(new Set());
    try {
      const [linkedRes, allRes] = await Promise.all([
        phappsRequest(buildGroupManagerLinkedChemistsPath(id), "GET"),
        phappsRequest(API_PATHS.GROUP_MANAGERS_ALL_CHEMISTS, "GET"),
      ]);
      const linked = extractArray(linkedRes, [
        "chemists",
        "linkedChemists",
        "items",
        "data",
        "results",
      ]);
      const all = extractArray(allRes, [
        "chemists",
        "items",
        "data",
        "results",
      ]);
      setLinkedChemists(linked);
      setAllChemists(all);
    } catch (e) {
      setDetailError(e.message || "Failed to load chemist data.");
      setLinkedChemists([]);
      setAllChemists([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedManager) {
      setLinkedChemists([]);
      setAllChemists([]);
      setDetailError(null);
      return;
    }
    loadManagerChemists(selectedManager);
  }, [selectedManager, loadManagerChemists]);

  const linkedIds = useMemo(() => {
    const s = new Set();
    for (const r of linkedChemists) {
      const n = Number(chemistRowId(r));
      if (Number.isFinite(n)) s.add(n);
    }
    return s;
  }, [linkedChemists]);

  const filteredLinked = useMemo(
    () => filterChemistRows(linkedChemists, linkedSearch),
    [linkedChemists, linkedSearch]
  );

  const availableToAdd = useMemo(() => {
    return allChemists.filter((c) => {
      const cid = chemistRowId(c);
      if (cid == null) return false;
      return !linkedIds.has(Number(cid));
    });
  }, [allChemists, linkedIds]);

  const toggleInSet = (set, id, on) => {
    const next = new Set(set);
    if (on) next.add(id);
    else next.delete(id);
    return next;
  };

  const selectAllVisibleLinked = () => {
    const ids = filteredLinked
      .map((r) => chemistRowId(r))
      .map(Number)
      .filter((n) => Number.isFinite(n));
    setLinkedSelected(new Set(ids));
  };

  const handleAddChemists = async (chemistIds) => {
    const mid = managerId(selectedManager);
    if (mid == null || !chemistIds?.length) return;
    setAddSaving(true);
    try {
      const body = {
        ManagerID: mid,
        ChemistIDs: chemistIds.filter(Number.isFinite),
      };
      const res = await phappsRequest(
        API_PATHS.GROUP_MANAGERS_ADD_CHEMISTS,
        "POST",
        body
      );
      const n = chemistIds.length;
      setAddChemistsOpen(false);
      await loadManagerChemists(selectedManager);
      await loadManagers();
      const msg =
        res?.addedCount != null
          ? `Added ${res.addedCount} chemist(s).`
          : `Added ${n} chemist(s).`;
      showAlert({ severity: "success", title: "Success", message: msg });
    } catch (e) {
      showAlert({
        severity: "error",
        title: "Error",
        message: e.message || "Add failed.",
      });
    } finally {
      setAddSaving(false);
    }
  };

  const handleRemoveChemists = async () => {
    const mid = managerId(selectedManager);
    if (mid == null || linkedSelected.size === 0) return;
    setRemoveSaving(true);
    try {
      const body = {
        ManagerID: mid,
        ChemistIDs: Array.from(linkedSelected).filter(Number.isFinite),
      };
      const res = await phappsRequest(
        API_PATHS.GROUP_MANAGERS_REMOVE_CHEMISTS,
        "POST",
        body
      );
      const n = linkedSelected.size;
      setLinkedSelected(new Set());
      await loadManagerChemists(selectedManager);
      await loadManagers();
      const msg =
        res?.removedCount != null
          ? `Removed ${res.removedCount} chemist(s).`
          : `Removed ${n} chemist(s).`;
      showAlert({ severity: "success", title: "Success", message: msg });
    } catch (e) {
      showAlert({
        severity: "error",
        title: "Error",
        message: e.message || "Remove failed.",
      });
    } finally {
      setRemoveSaving(false);
    }
  };

  const openEdit = (m) => {
    setEditTarget(m);
    setEditOpen(true);
  };

  const handleDeleteManager = async (m) => {
    const id = managerId(m);
    if (id == null || deleting) return;
    const label = m.Name || m.Email || `manager ${id}`;
    const links = Number(m.LinkedChemistCount || 0);
    const ok = window.confirm(
      `Delete ${label}? This permanently removes their portal access${
        links ? ` and their ${links} linked chemist(s)` : ""
      }. This cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await phappsRequest(buildGroupManagerDeletePath(id), "DELETE");
      if (selectedManager && managerId(selectedManager) === id) {
        setSelectedManager(null);
      }
      await loadManagers();
      showAlert({
        severity: "success",
        title: "Deleted",
        message: `${label} has been deleted.`,
      });
    } catch (e) {
      showAlert({
        severity: "error",
        title: "Error",
        message: e.message || "Delete failed.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const onCreateSuccess = async (res) => {
    await loadManagers();
    const extra =
      res?.message ||
      res?.inviteMessage ||
      res?.emailSentMessage ||
      (res?.emailSent ? "Invitation email sent." : "");
    showAlert({
      severity: "success",
      title: "Manager created",
      message: extra || "The manager list has been refreshed.",
    });
  };

  const onEditSaved = async () => {
    await loadManagers();
    showAlert({
      severity: "success",
      title: "Saved",
      message: "Manager updated.",
    });
  };

  const selId = selectedManager ? managerId(selectedManager) : null;
  const selectedLabel =
    selectedManager?.Name ||
    selectedManager?.Email ||
    "Selected manager";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {AlertSnackbar}

      {/* ── Fixed header ── */}
      <div className="flex-shrink-0 px-6 pt-5 pb-4 w-full max-w-5xl mx-auto">
        <div className="mb-3">
          <Link
            to="/admin"
            className="text-sm font-medium text-[#002169] hover:underline"
          >
            ← Admin
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Group Managers</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Create and manage portal group managers, permissions, and linked chemists.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              {[
                { key: "managers", label: "Managers" },
                { key: "roster", label: "Manager Options" },
              ].map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setView(v.key)}
                  className={`px-3 py-2 text-sm font-semibold transition-colors ${
                    view === v.key
                      ? "bg-[#002169] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className={`px-4 py-2 text-sm ${btnNavy}`}
            >
              Create manager
            </button>
          </div>
        </div>
      </div>

      {view === "roster" ? (
        /* Capped width — full-bleed spreads the toggle columns too far apart */
        <div className="flex-1 min-h-0 px-6 pb-6 w-full max-w-5xl mx-auto">
          <GroupManagerRoster
            managers={visibleManagers}
            viewerIsPHAdmin={viewerIsPHAdmin}
            ownManagerId={ownManagerId}
            onUpdated={(updated) =>
              setManagers((prev) =>
                prev.map((m) => (managerId(m) === managerId(updated) ? updated : m))
              )
            }
            onError={(msg) => showAlert({ severity: "error", title: "Error", message: msg })}
            onNotice={(msg) => showAlert({ severity: "success", title: "Slack", message: msg })}
          />
        </div>
      ) : (
      <div className="flex-1 min-h-0 px-6 pb-6 w-full max-w-5xl mx-auto">
        <div
          className={`grid grid-cols-1 gap-6 h-full min-w-0 ${
            selectedManager ? "lg:grid-cols-2" : ""
          }`}
        >
          {/* ── Managers column: PH Admin grid stacked above Group Staff grid ── */}
          <div className="min-h-0 overflow-y-auto flex flex-col gap-4 pr-1">
            <div className="flex items-center justify-end">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                  className="rounded border-gray-300 text-[#002169] focus:ring-[#002169]"
                />
                Show active only
              </label>
            </div>

            {managersError && (
              <div className="p-4 text-sm text-red-700 bg-red-50 rounded-xl">
                {managersError}
              </div>
            )}

            {managersLoading ? (
              <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
            ) : (
              <>
                <ManagerTable
                  title="Pharmacy Halo Admin"
                  rows={phAdmins}
                  emptyText="No Pharmacy Halo admins match this filter."
                  showDot
                  showChemistCount={false}
                  selectedId={selId}
                  ownManagerId={ownManagerId}
                  onSelect={setSelectedManager}
                  onEdit={openEdit}
                  onDelete={handleDeleteManager}
                />
                <ManagerTable
                  title="Pharmacy Group Staff"
                  rows={groupStaff}
                  emptyText="No group staff match this filter."
                  showDot={false}
                  showChemistCount
                  selectedId={selId}
                  ownManagerId={ownManagerId}
                  onSelect={setSelectedManager}
                  onEdit={openEdit}
                  onDelete={handleDeleteManager}
                />
              </>
            )}
          </div>

          {/* ── Linked chemists: only once a manager has been clicked ── */}
          {selectedManager && (
            <section className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden flex flex-col min-w-0 min-h-0">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2 shrink-0">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    Linked chemists
                  </h2>
                  <p className="text-xs text-gray-500 truncate">{selectedLabel}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setAddChemistsOpen(true)}
                    disabled={detailLoading}
                    className={`px-3 py-1.5 text-sm ${btnNavy}`}
                  >
                    Add chemists
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedManager(null)}
                    title="Close"
                    aria-label="Close linked chemists"
                    className="w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col p-4 overflow-hidden">
                {detailError ? (
                  <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    {detailError}
                  </div>
                ) : detailLoading ? (
                  <div className="text-center text-gray-500 text-sm py-10">
                    Loading chemists…
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-2 mb-3 shrink-0">
                      <input
                        type="search"
                        placeholder="Filter by name…"
                        value={linkedSearch}
                        onChange={(e) => setLinkedSearch(e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={selectAllVisibleLinked}
                          className={`px-3 py-1.5 text-xs font-medium ${btnGhostNavy}`}
                        >
                          Select all visible
                        </button>
                        <button
                          type="button"
                          onClick={() => setLinkedSelected(new Set())}
                          disabled={linkedSelected.size === 0}
                          className={`px-3 py-1.5 text-xs font-medium ${btnGhostNavy}`}
                        >
                          Clear selection
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveChemists}
                          disabled={removeSaving || linkedSelected.size === 0}
                          className="ml-auto px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {removeSaving
                            ? "Removing…"
                            : `Remove selected (${linkedSelected.size})`}
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-auto">
                      {filteredLinked.length === 0 ? (
                        <p className="px-3 py-8 text-center text-gray-500 text-sm">
                          {linkedChemists.length === 0
                            ? "No chemists linked yet."
                            : "No linked chemists match this filter."}
                        </p>
                      ) : (
                        <ul className="divide-y divide-gray-100 text-sm">
                          {filteredLinked.map((r) => {
                            const cid = chemistRowId(r);
                            const idn = Number(cid);
                            const valid = Number.isFinite(idn);
                            const checked = valid && linkedSelected.has(idn);
                            return (
                              <li key={r.LinkID ?? `${cid}-link`}>
                                <label className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-gray-50">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={!valid}
                                    onChange={(e) =>
                                      valid &&
                                      setLinkedSelected(
                                        toggleInSet(
                                          linkedSelected,
                                          idn,
                                          e.target.checked
                                        )
                                      )
                                    }
                                    className="rounded border-gray-300 text-[#002169] focus:ring-[#002169]"
                                  />
                                  <span className="text-gray-900 truncate whitespace-nowrap">
                                    {r.ChemistName ?? r.chemistName ?? "—"}
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
      )}

      <AddGroupManagerChemistsModal
        isOpen={addChemistsOpen}
        onClose={() => !addSaving && setAddChemistsOpen(false)}
        managerLabel={selectedLabel}
        chemists={availableToAdd}
        saving={addSaving}
        onConfirm={handleAddChemists}
      />

      <CreateGroupManagerModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={onCreateSuccess}
      />
      <EditGroupManagerModal
        isOpen={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditTarget(null);
        }}
        manager={editTarget}
        onSaved={onEditSaved}
      />
    </div>
  );
};

export default GroupManagersPage;
